import { Badge, Card, Col, Row, Modal } from "react-bootstrap";
import classNames from "classnames";
import AnimatedNumbers, { Props } from "react-animated-numbers";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { DasboardWidgetTitle } from "components/DasboardWidgetTitle";
import { useDebounce } from "hooks/useDebounce";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { useSearchParams } from "react-router-dom";
import { ZTooltip } from "components/ZTooltip";
import { useAppSelector } from "redux/hooks";
import Spinner from "components/Spinner";

export type panelKeyValueType = { name: string, value: string, action?: () => void };

type StatisticsWidgetProps = {
    textClass?: string;
    bgClass?: string;
    icon?: string;
    title: string;
    description?: string;
    drilldownKeyword?: string;
    stats?: number;
    isMoney?: boolean;
    isPercent?: boolean;
    footerComponent?: React.ReactNode;
    trend?: {
        value?: number;
        time?: string;
        tooltip?: string;
    };
    extraValuePrefix?: string;
    extraValueSuffix?: string;
    extraValue?: number,

    leftPanel?: panelKeyValueType[] | JSX.Element,
    rightPanel?: panelKeyValueType[] | JSX.Element,

    style?: CSSProperties,
    separatorColour?: string

    children?: React.ReactNode,

    enableDrillDown?: boolean;
    drillDownClicked?: () => boolean;
    drilldownSize?: "sm" | "md" | "lg" | "xl";
};

const ZAnimatedNumbers = (props: Props) => {
    return (
        <AnimatedNumbers animateToNumber={0} />
    );
}

const StatisticsWidget = ({ textClass, bgClass, icon, title, stats, trend, description, drilldownKeyword, isMoney, isPercent, separatorColour, style, extraValuePrefix, extraValueSuffix, extraValue, children, enableDrillDown, drillDownClicked, drilldownSize, leftPanel, rightPanel, footerComponent }: StatisticsWidgetProps) => {
    const [key, setKey] = useState(0);
    const [modelOpen, setModelOpen] = useState(false);
    const debouncedUpdate = useDebounce(() => setKey((prev) => prev + 1), 200);
    const [searchParams, setSearchParams] = useSearchParams();
    const dashboard = useAppSelector((state) => state.dashboard);

    useEffect(() => {

        window.addEventListener('resize', debouncedUpdate);
        return () => {
            window.removeEventListener('resize', debouncedUpdate);
        };
    }, []);

    useEffect(() => {
        if (searchParams.get("drillDown") === drilldownKeyword && !dashboard.loading) {
            drillDownClicked && drillDownClicked();
            setModelOpen(true);
        } else {
            if (modelOpen) {
                setModelOpen(false);
            }
        }
    }, [searchParams, dashboard.loading]);


    function getPanel(item: panelKeyValueType[] | JSX.Element | undefined): JSX.Element | JSX.Element[] {
        if (item) {
            if (Array.isArray(item)) {
                const array = item as panelKeyValueType[];

                return array.map((e, i) => (
                    <div key={i} className={classNames("fw-normal", "mt-0", "primary-title-color")} onClick={() => e.action && e.action()} style={e.action ? {cursor: 'pointer', display: "flex", flex: "1"} : { display: "flex", flex: "1"}}>
                        <div style={{ flex: 1 }}>
                            <span>{e.name}</span>
                        </div>
                        <div>{e.value}</div>
                    </div>
                ))
            } else { //Must be a jsx element itself
                return item;
            }
        }
        return (<></>)
    }

    const compareText = trend ? <span className="primary-title-color font-weight-bold user-select-none"> {trend.time}</span> : null;
    const tooltipText = trend && trend.tooltip ? <ZTooltip text={trend.tooltip} className="d-inline-flex align-items-center ps-1"><ZIcon icon={ZIconType.Information} size="14px"/></ZTooltip> : null;

    return (
        <span style={{ display: 'grid', height: '100%' }}>

            <Card className={classNames("widget-flat", "mb-0", bgClass)} style={style}>
                <Card.Body className="d-flex flex-column">
                    <Row>
                        <Col xl={12} className="ps-0 pe-0 d-inline-flex mb-1 gap-1">
                            <DasboardWidgetTitle title={title} icon={icon} />
                            
                            {children && <div onClick={() => {
                                if (drillDownClicked) {
                                    if (drilldownKeyword) {
                                        setSearchParams({ drillDown: drilldownKeyword });
                                    }
                                    drillDownClicked() && setModelOpen(true);
                                } else {
                                    setModelOpen(true);
                                }
                            }} className={`ms-auto ${!enableDrillDown ? "invisible" : ""}`} style={{ cursor: 'pointer' }}><ZTooltip text="Drill down"><ZIcon icon={ZIconType.DrillDown} size="18px" /></ZTooltip></div>}

                        </Col>
                    </Row>
                    <Row className="h-fit">
                        {leftPanel ? (
                            <Col xl={rightPanel ? 6 : 12} className="pe-2 pb-2 pb-sm-0 ps-0 ps-sm-2 dash-widget-text" style={separatorColour ? { borderColor: separatorColour, display: "flex", flexDirection: "column" } : {}}>
                                <div style={{ flex: "1" }} />
                                {getPanel(leftPanel)}
                                <div style={{ flex: "1" }} />
                            </Col>
                        ) :
                            (
                                <Col xl={6} className="ps-0">
                                    <h3 className={classNames("mb-1",  "pt-0", "pt-sm-1", textClass ? textClass : "primary-title-color")} style={{ display: "flex", flexWrap: "wrap" }}>
                                        {isMoney && <span>$</span>}
                                        <AnimatedNumbers key={`type-${key}`} includeComma animateToNumber={stats ?? 0}></AnimatedNumbers>{isPercent && <span>%</span>}
                                        &nbsp;
                                        {extraValue != null && <span className="d-inline-flex">{<span>({extraValuePrefix}</span>}<AnimatedNumbers key={`type-${key + 1}`} includeComma animateToNumber={extraValue}></AnimatedNumbers><span>{extraValueSuffix}</span>{<span>)</span>}</span>}
                                    </h3>
                                </Col>
                            )}

                        {rightPanel && (
                            <Col xl={6} className="hide-when-lower-than-xl pe-0 pb-2 pb-sm-0 ps-0 ps-sm-2 dash-widget-text" style={separatorColour ? { borderColor: separatorColour, display: "flex", flexDirection: "column" } : {}}>
                                <div style={{ flex: "1" }} />
                                {getPanel(rightPanel)}
                                <div style={{ flex: "1" }} />
                            </Col>
                        )}

                    </Row>
                    <Row className="flex-grow">
                        <span style={{ display: 'flex', flex: 1, padding: 0, marginTop: 'auto' }} >
                            {trend && (
                                <p className={classNames("ps-0", "flex-grow-1", textClass)} style={{ fontSize: "14px", marginBottom: "0px" }}>
                                    {/* @ts-ignore */}
                                    <i className={classNames("mdi", trend.value > 0 ? "mdi-arrow-up-bold text-primary" : "text-danger mdi-arrow-down-bold")}></i>
                                    {/* @ts-ignore */}
                                    <span className={classNames("font-weight-bold", trend.value > 0 ? "text-primary" : "text-danger")} >{trend.value?.toFixed(2)}{"%"}</span>
                                    {compareText}
                                    {tooltipText}
                                </p>
                            )}
                            {!trend && (
                                <p className={classNames("ps-0", textClass)} style={{ fontSize: "14px", marginBottom: "-4px" }}>
                                    <span className="primary-title-color font-weight-bold">&nbsp;</span>
                                </p>
                            )}
                        </span>
                    </Row>
                    {footerComponent != null && 
                        <Row className="mt-auto d-flex flex-column gap-1">
                            <Col xs={12} className="d-inline-flex align-items-center px-0 gap-1">
                                {footerComponent}
                            </Col>
                        </Row>
                    }
                </Card.Body>
            </Card>

            <Modal
                show={modelOpen}
                onHide={() => {
                    searchParams.delete("drillDown");
                    setSearchParams(searchParams);
                    setModelOpen(false);
                }}
                centered={true}
                size={drilldownSize === "md" ? undefined : drilldownSize ?? "xl"}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="d-flex align-items-center justify-content-center w-100" style={{ height: '70vh' }}>
                        {dashboard.loading ? <Spinner size="lg" /> : children}
                    </div>
                </Modal.Body>
            </Modal>
        </span>
    );
};

export default StatisticsWidget;
